import { every, flow, isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { AgentOfficeBulletinList } from 'screens/AgentOfficePages/common/sections/listing';
import {
  createIsLoadingSelector,
  officeByIdSelector,
  makeNullSafeDomainGetter,
  teamByOfficeIdSelector,
  officeBulletinsByIdSelector,
  publicRealEstateOfficeByIdSelector,
} from 'store/state/domainData/selectors/common';
import { agentBulletinsDataSelector } from './agentById';
import { Route } from 'config/routes';
import { routeSelector } from 'store/state/selectors/router';
import { IAgentOfficeDeal, DealAffiliationType, ITeamByOfficeIdData, IStaffMember } from 'utils/entities';
import uuid from 'uuid/v4';
import { UiAgentOfficeDeal } from 'screens/AgentOfficePages/common/sections/deals/types';
import { uniqBy, some } from 'lodash';

export const safeOfficeBulletinsByIdSelector = makeNullSafeDomainGetter(officeBulletinsByIdSelector, 'getRealEstateOfficeById');
export const teamByOfficeIdDataSelector = makeNullSafeDomainGetter(teamByOfficeIdSelector, 'getPublicRealEstateAgentsByOfficeId');
export const isOfficeLoadingSelector = createIsLoadingSelector(officeByIdSelector);
export const officePageDomainDataSelector = makeNullSafeDomainGetter(officeByIdSelector, 'getRealEstateOfficeById');
export const publicOfficePageDataSelector = makeNullSafeDomainGetter(publicRealEstateOfficeByIdSelector, 'getPublicRealEstateOfficeById');
export const isPublicOfficeLoadingSelector = createIsLoadingSelector(publicRealEstateOfficeByIdSelector);
export const isPremiumOfficeSelector = flow(publicOfficePageDataSelector, office => office && office.displayLevel === 'premium');
export const safeOfficeDataForSoldDealBannerSelector = makeNullSafeDomainGetter(publicRealEstateOfficeByIdSelector, 'getPublicRealEstateOfficeById');

export function sortAgentsByOrder(ids: ITeamByOfficeIdData[], orderData: IStaffMember[]): ITeamByOfficeIdData[] {
  const orderMap = new Map(orderData.map(({ name, description }) => [ name, Number(description) ]));

  return [ ...ids ].sort((a, b) => {
    const orderA = orderMap.get(a.agentId);
    const orderB = orderMap.get(b.agentId);

    if (orderA === undefined && orderB === undefined) return 0;
    if (orderA === undefined) return 1;
    if (orderB === undefined) return -1;

    return orderA - orderB;
  });
}

export const ORDER_AGENT_ROLE = 'agent_index';
export const officePageDataSelector = createSelector([
  officePageDomainDataSelector,
], (office) => {
  if (office) {
    return {
      ...office,
      staffMembers: (office.staffMembers || []).filter(m => m.role !== ORDER_AGENT_ROLE),
      agentOrderList: (office.staffMembers || []).filter(m => m.role === ORDER_AGENT_ROLE),
    };
  }

  return office;
});

export const officeIdSelector = flow(publicOfficePageDataSelector, officeData => officeData ? officeData.officeId : null);
export const officePageIdSelector = flow(officePageDataSelector, officeData => officeData ? officeData.officeId : null);
export const uniqOfficeStaffPageIdsSelector = createSelector([
  officePageDataSelector,
  teamByOfficeIdDataSelector,
], (officeData, teamData) => {
  const staffMembersUniq = officeData ? (officeData.staffMembers || []).map(m => (m.name + m.role)) : [];
  const agentIds = (teamData || []).map(t => t.agentId);

  return [ ...staffMembersUniq, ...agentIds ];
});

export const officeBulletinsDataSelector = createSelector([
  safeOfficeBulletinsByIdSelector,
  officePageDataSelector,
  teamByOfficeIdDataSelector,
], (listings, officeData, agentsInOffice) => {
  if (!listings) return null;

  const agentsInOfficeIds = new Set((agentsInOffice || []).map(item => item && item.agentId));

  const dealsByAgent = officeData && listings.deals && listings.deals.length ? (
    listings.deals.reduce((acc, d: IAgentOfficeDeal) => {
      if (!d.location) return acc;

      const agentList = d.agents ? d.agents.map(a => a.officeId === officeData.officeId ? ({
        ...a,
        ...(!agentsInOfficeIds.has(a.agentId) ? { agentName: null } : {}),
      }) : null).filter(Boolean) : [];
      if (!agentList.length) return acc;

      const dealsWithSameAgent = agentList.map(a => ({
        ...d,
        agents: [ a ],
      }));
      return [ ...acc, ...dealsWithSameAgent ];
    }, [])
  ) : [];

  return {
    ...listings,
    deals: dealsByAgent,
  };
});

export const officePageBulletinSelector = createSelector([ officeBulletinsDataSelector, officePageDataSelector ], (office, officePageData) => {
  if (!office) return [];
  const dealsEnriched = office.deals ? office.deals.reduce((acc, d: IAgentOfficeDeal) => {
    if (!d.location) return acc;

    const agentList = d.agents ? d.agents.filter(a => a.officeId === officePageData.officeId) : [];
    if (!agentList.length) return acc;

    const dealsWithSameAgent = agentList.map(a => ({
      ...d,
      agents: [ a ],
      type: 'sold',
      locationPoint: { lng: d.location.coordinates[0], lat: d.location.coordinates[1] },
      dealType: 'sold',
      id: d.saleId,
    }));
    return [ ...acc, ...dealsWithSameAgent ];
  }, []) : [];
  return [ ...(office.bulletins || []), ...(office.commercialBulletins || []), ...dealsEnriched ];
});
export const agentOfficePageBulletinByDealTypeAndMarketplaceSelector = createSelector([ routeSelector, officePageDataSelector, agentBulletinsDataSelector, officeBulletinsDataSelector ], (route, officePageData, agentBulletins, officeBulletins) => {
  if (!officePageData) return null;

  const poi = route.name === Route.AgentPage
    ? (officePageData.allOfficeBulletinsInAgentPage ? officeBulletins : agentBulletins)
    : officeBulletins;
  const result = [ ...poi.bulletins, ...poi.commercialBulletins ].reduce<AgentOfficeBulletinList>((acc, bulletin) => {
    switch (`${bulletin.type}_${bulletin.dealType}`) {
      case 'bulletin_unitBuy':
        acc.sale.push(bulletin);
        break;
      case 'bulletin_unitRent':
        acc.rent.push(bulletin);
        break;
      case 'commercialBulletin_unitBuy':
        acc.commercialSale.push(bulletin);
        break;
      case 'commercialBulletin_unitRent':
        acc.commercialRent.push(bulletin);
        break;
    }
    return acc;
  }, { sale: [], rent: [], commercialSale: [], commercialRent: [] });
  if (every(result, isEmpty)) return null;

  return result;
});

export const agentOfficePageDealsSelector = createSelector([
  safeOfficeBulletinsByIdSelector,
  officePageDataSelector,
  teamByOfficeIdDataSelector,
], (listings, officeData, agentsInOffice): UiAgentOfficeDeal[] => {
  if (!listings || !Array.isArray(listings.deals)) return [];

  const agentsInOfficeIds = new Set((agentsInOffice || []).map(item => item && item.agentId));

  return listings.deals.reduce((acc, d: IAgentOfficeDeal) => {
    if (!d.location) return acc;
    const sellersList = Array.isArray(d.sellers) && d.sellers.filter(a => a.officeId === officeData.officeId) || [];
    const buyersList = Array.isArray(d.buyers) && d.buyers.filter(a => a.officeId === officeData.officeId) || [];

    const agentList = d.agents ? uniqBy(d.agents, 'agentId').map(a => a.officeId === officeData.officeId ? ({
      ...a,
      ...(!agentsInOfficeIds.has(a.agentId) ? { agentName: null } : {}),
    }) : null).filter(Boolean) : [];
    if (!agentList.length) return acc;

    const dealsToInclude = agentList.reduce((nextDeals, agent) => {
      const isSeller = some(sellersList, (seller => seller.agentId === agent.agentId));
      const isBuyer = some(buyersList, (buyer => buyer.agentId === agent.agentId));

      let side: DealAffiliationType;
      switch (true) {
        case isSeller && isBuyer:
          side = DealAffiliationType.Both;
          break;
        case isBuyer:
          side = DealAffiliationType.Buyer;
          break;
        case isSeller:
          side = DealAffiliationType.Seller;
          break;
        default:
      }
      return [ ...nextDeals, {
        ...d,
        key: uuid(),
        agents: [ agent ],
        side,
      } ];
    }, []);

    return [ ...acc, ...dealsToInclude ];
  }, []).sort((a, b) => +new Date(b.date) - +new Date(a.date));
});

export const officeDataForSoldDealBannerSelector = createSelector(safeOfficeDataForSoldDealBannerSelector, (office) => {
  if (!office) return null;

  return {
    officeId: office.officeId,
    officeName: office.officeName,
    iconUrl: office.iconUrl,
    virtualPhone: office.virtualPhone,
    phone1: office.phone1,
    officeConnectionInAgentPage: office.officeConnectionInAgentPage,
  };
});
